import React, { useEffect, useState } from 'react';
import { CheckCircle2, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { staffService } from '../../services/staffService';
import { OrderPhotoUpload } from './OrderPhotoUpload';
import { PhotoProofGallery } from './PhotoProofGallery';
import { LocationLine } from '../LocationLine';

export const CompleteDeliveryDialog = ({ open, onOpenChange, stop, routeId, stopIndex, onCompleted }) => {
  const orderId = stop?.order_id;
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !stop) return;
    setPhotos(stop.delivery_photos || []);
    setNotes(stop.notes || '');
  }, [open, stop]);

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      await staffService.updateDeliveryStop(routeId, stopIndex, { status: 'delivered', notes });
      toast.success('Delivery completed', { position: 'top-center' });
      onCompleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to complete delivery', { position: 'top-center', duration: 4000 });
    } finally {
      setSubmitting(false);
    }
  };

  if (!stop) return null;

  const changedCount = (stop.pickup_reconciliation?.lines || []).filter((l) => l.delta !== 0).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-lg max-h-[92vh] overflow-y-auto p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-2 sm:px-6">
          <DialogTitle className="text-base sm:text-lg">Complete delivery — {stop.order_number}</DialogTitle>
          <DialogDescription className="text-left">
            Take at least one photo of the delivered items. The client sees these as proof of delivery.
          </DialogDescription>
          <div className="text-left pt-1">
            <p className="text-sm font-semibold text-gray-900">{stop.client_name}</p>
            <LocationLine
              name={stop.location_name}
              source={stop.location_name_source}
              clientName={stop.client_name}
              address={stop.delivery_address}
              testId={`complete-location-${orderId}`}
            />
          </div>
        </DialogHeader>

        <div className="px-4 sm:px-6 pb-4 space-y-4">
          {stop.pickup_reconciliation ? (
            changedCount > 0 && (
              <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex gap-2">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                Pickup count recorded — {changedCount} item{changedCount === 1 ? '' : 's'} changed. The invoice will bill delivered quantities.
              </div>
            )
          ) : (
            <div className="p-2.5 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800 flex gap-2" data-testid={`no-count-hint-${orderId}`}>
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              No pickup count was recorded — the invoice will bill the ordered quantities.
            </div>
          )}

          <OrderPhotoUpload
            orderId={orderId}
            kind="delivery"
            testId={`delivery-photo-${orderId}`}
            photos={photos}
            onPhotosChange={setPhotos}
            canRemove
            label="Delivery photos"
          />
          {photos.length === 0 && (
            <p className="text-xs text-amber-600" data-testid={`delivery-photo-required-${orderId}`}>
              Add at least 1 photo of the delivered items to complete this stop
            </p>
          )}

          {/* What left the kitchen, for a side-by-side check on quantities */}
          {(stop.production_photos || []).length > 0 && (
            <div className="pt-2 border-t" data-testid={`complete-production-photos-${orderId}`}>
              <PhotoProofGallery
                testIdPrefix={`complete-proof-${orderId}`}
                groups={[{
                  key: 'production',
                  title: 'Packed in the kitchen',
                  caption: 'what left the bakery',
                  photos: stop.production_photos || [],
                }]}
              />
            </div>
          )}

          <div>
            <Label className="text-sm">Delivery notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Left with front desk, signed by manager…"
              className="mt-1"
              rows={2}
              data-testid={`delivery-notes-${orderId}`}
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-4 py-3 sm:px-6 flex justify-end gap-2">
          <Button variant="outline" className="h-11" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleComplete}
            disabled={photos.length === 0 || submitting}
            className="h-11 px-6 bg-green-600 hover:bg-green-700"
            data-testid={`complete-delivery-${orderId}`}
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {submitting ? 'Completing…' : 'Complete delivery'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
